import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  DialogBody,
  Button,
  RadioGroup,
  Radio,
  Dropdown,
  Option,
  Checkbox,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { PeopleRegular, PersonRegular, BuildingRegular } from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { resolveAttendees } from '../services/api';
import type {
  AppointmentAttendeesResponse,
  AppointmentAttendee,
  ResolveAttendeesRequest,
  AttendeeSelection,
  TaxReturn,
} from '../types';

interface AttendeeResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attendeesData: AppointmentAttendeesResponse;
  taxYear: number;
  onResolved: (returns: TaxReturn[]) => void;
}

export default function AttendeeResolutionDialog({
  open,
  onOpenChange,
  attendeesData,
  taxYear,
  onResolved,
}: AttendeeResolutionDialogProps) {
  const queryClient = useQueryClient();

  // Filter to only contacts (individuals) for joint/separate decision
  const contacts = attendeesData.attendees.filter(a => a.partyType === 'contact');
  const accounts = attendeesData.attendees.filter(a => a.partyType === 'account');

  // Determine if this is a business-only scenario
  const isBusinessOnly = contacts.length === 0 && accounts.length > 0;
  const isSingleContact = contacts.length === 1 && accounts.length === 0;
  const needsResolutionChoice = contacts.length >= 2;

  const [resolutionType, setResolutionType] = useState<'joint' | 'separate'>('joint');
  const [taxpayerId, setTaxpayerId] = useState<string>('');
  const [spouseId, setSpouseId] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [savePreference, setSavePreference] = useState(true);

  // Auto-select first two contacts if available, or first account for business
  useEffect(() => {
    if (contacts.length >= 2) {
      setTaxpayerId(contacts[0].partyId);
      setSpouseId(contacts[1].partyId);
    } else if (contacts.length === 1) {
      setTaxpayerId(contacts[0].partyId);
    }

    // Auto-select first account for business returns
    if (accounts.length > 0) {
      setSelectedAccountId(accounts[0].partyId);
    }
  }, [contacts, accounts]);

  const resolveMutation = useMutation({
    mutationFn: async (request: ResolveAttendeesRequest) => {
      return resolveAttendees(attendeesData.appointmentId, request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['taxReturns'] });
      onResolved(data);
      onOpenChange(false);
    },
  });

  const getAttendeeSelection = (attendee: AppointmentAttendee): AttendeeSelection => ({
    contactId: attendee.partyId,
    contactName: attendee.partyName,
    email: attendee.email,
    phone: attendee.phone,
  });

  const handleResolve = () => {
    // Handle business-only scenario
    if (isBusinessOnly) {
      const account = accounts.find(a => a.partyId === selectedAccountId);
      const request: ResolveAttendeesRequest = {
        resolutionType: 'separate', // Business returns are always "separate"
        taxpayer: account ? {
          contactId: account.partyId,
          contactName: account.partyName,
          email: account.email,
          phone: account.phone,
        } : undefined,
        savePreference: false,
        taxYear,
      };
      resolveMutation.mutate(request);
      return;
    }

    // Handle single contact scenario
    if (isSingleContact) {
      const contact = contacts[0];
      const request: ResolveAttendeesRequest = {
        resolutionType: 'separate',
        taxpayer: getAttendeeSelection(contact),
        savePreference: false,
        taxYear,
      };
      resolveMutation.mutate(request);
      return;
    }

    // Handle multi-contact scenario (joint/separate)
    const taxpayer = contacts.find(c => c.partyId === taxpayerId);
    const spouse = contacts.find(c => c.partyId === spouseId);

    const request: ResolveAttendeesRequest = {
      resolutionType,
      taxpayer: taxpayer ? getAttendeeSelection(taxpayer) : undefined,
      spouse: resolutionType === 'joint' && spouse ? getAttendeeSelection(spouse) :
              resolutionType === 'separate' && spouse ? getAttendeeSelection(spouse) : undefined,
      savePreference,
      taxYear,
    };

    resolveMutation.mutate(request);
  };

  // Determine if the form is valid for submission
  const canSubmit = isBusinessOnly
    ? !!selectedAccountId
    : isSingleContact
    ? true
    : !!taxpayerId;

  // Determine button text
  const getButtonText = () => {
    if (isBusinessOnly) return '1 Business Return';
    if (isSingleContact) return '1 Individual Return';
    if (resolutionType === 'joint') return '1 Return';
    return `${contacts.length} Returns`;
  };

  const taxpayerOptions = contacts.filter(c => c.partyId !== spouseId);
  const spouseOptions = contacts.filter(c => c.partyId !== taxpayerId);

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '500px' }}>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PeopleRegular />
              Resolve Attendees
            </div>
          </DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: 'var(--tl-text-secondary)', marginBottom: '8px' }}>
                Appointment: <strong>{attendeesData.appointmentSubject}</strong>
              </p>
              <p style={{ color: 'var(--tl-text-secondary)', marginBottom: '16px' }}>
                {isBusinessOnly
                  ? 'This appointment is for a business entity.'
                  : isSingleContact
                  ? 'This appointment is for a single individual.'
                  : 'This appointment has multiple individuals. How should they be filed?'}
              </p>
            </div>

            {/* List of attendees */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '8px' }}>Attendees Found:</h4>
              {contacts.map(attendee => (
                <div
                  key={attendee.partyId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: 'var(--tl-bg-subtle)',
                    borderRadius: '4px',
                    marginBottom: '4px'
                  }}
                >
                  <PersonRegular />
                  <div>
                    <div style={{ fontWeight: 500 }}>{attendee.partyName}</div>
                    {attendee.email && (
                      <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>{attendee.email}</div>
                    )}
                  </div>
                </div>
              ))}
              {accounts.map(attendee => (
                <div
                  key={attendee.partyId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    marginBottom: '4px'
                  }}
                >
                  <BuildingRegular />
                  <div>
                    <div style={{ fontWeight: 500 }}>{attendee.partyName}</div>
                    <Badge appearance="outline" color="success">Business</Badge>
                  </div>
                </div>
              ))}
            </div>

            {/* Business-only message */}
            {isBusinessOnly && (
              <div style={{
                padding: '12px',
                backgroundColor: '#e8f5e9',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, color: '#2e7d32' }}>
                  A business tax return will be created for <strong>{accounts[0]?.partyName}</strong>.
                </p>
              </div>
            )}

            {/* Single contact message */}
            {isSingleContact && (
              <div style={{
                padding: '12px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, color: '#1565c0' }}>
                  An individual tax return will be created for <strong>{contacts[0]?.partyName}</strong>.
                </p>
              </div>
            )}

            {/* Resolution options - only show if there are 2+ contacts */}
            {needsResolutionChoice && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '8px' }}>Filing Type:</h4>
                  <RadioGroup
                    value={resolutionType}
                    onChange={(_, data) => setResolutionType(data.value as 'joint' | 'separate')}
                  >
                    <Radio
                      value="joint"
                      label={
                        <div>
                          <strong>Married Filing Jointly (MFJ)</strong>
                          <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                            Create ONE return with both taxpayer and spouse
                          </div>
                        </div>
                      }
                    />
                    <Radio
                      value="separate"
                      label={
                        <div>
                          <strong>Separate Returns</strong>
                          <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                            Create SEPARATE returns for each individual
                          </div>
                        </div>
                      }
                    />
                  </RadioGroup>
                </div>

                {/* Taxpayer/Spouse selection for joint filing */}
                {resolutionType === 'joint' && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '8px' }}>Designation:</h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                          Taxpayer (Primary)
                        </label>
                        <Dropdown
                          value={taxpayerId}
                          onOptionSelect={(_, data) => setTaxpayerId(data.optionValue || '')}
                          style={{ width: '100%' }}
                        >
                          {taxpayerOptions.map(c => (
                            <Option key={c.partyId} value={c.partyId} text={c.partyName}>
                              {c.partyName}
                            </Option>
                          ))}
                        </Dropdown>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                          Spouse
                        </label>
                        <Dropdown
                          value={spouseId}
                          onOptionSelect={(_, data) => setSpouseId(data.optionValue || '')}
                          style={{ width: '100%' }}
                        >
                          {spouseOptions.map(c => (
                            <Option key={c.partyId} value={c.partyId} text={c.partyName}>
                              {c.partyName}
                            </Option>
                          ))}
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Save preference checkbox - only show for multi-contact resolution */}
            {needsResolutionChoice && (
              <div style={{ marginTop: '16px' }}>
                <Checkbox
                  checked={savePreference}
                  onChange={(_, data) => setSavePreference(data.checked === true)}
                  label="Remember this preference for future appointments"
                />
              </div>
            )}

            {/* Error display */}
            {resolveMutation.isError && (
              <div style={{
                padding: '12px',
                marginTop: '16px',
                backgroundColor: '#fde7e9',
                borderRadius: '4px',
                color: '#a4262c'
              }}>
                {resolveMutation.error?.message || 'Failed to resolve attendees'}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={handleResolve}
              disabled={resolveMutation.isPending || !canSubmit}
            >
              {resolveMutation.isPending ? (
                <>
                  <Spinner size="tiny" style={{ marginRight: '8px' }} />
                  Creating...
                </>
              ) : (
                `Create ${getButtonText()}`
              )}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
